/**
 * Manufacturing management → Incoming orders.
 *
 * Port of Manufacturing Dashboard (**Appsmith**) page **Incoming Orders** — slug `incoming-orders` —
 * query `get_unfilled_sales_orders` (`Smart-Grid-Solutions/Manufacturing-Dashboard`).
 *
 * Open sales orders (not Cancelled or Pending) whose lines still need batch allocation: SKU lines where
 * `quantity - SUM(batch_item.quantity) > 0`, excluding Shipping* and %Fee% SKUs, match the Dashboard SQL.
 */

import { useMemo, useState } from "react";

import { HubDataTable, HubDataTextCell, type HubDataTableColumn } from "@/components/hub/HubDataTable";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { HubPageTitle } from "@/components/HubPageTitle";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { salesOrderStatusLabel } from "@/lib/salesOrderHubStatus";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { buildManufacturingEditBatchPath } from "@/lib/manufacturingEditBatchPath";
import { MANUFACTURING_HUB_READ_ONLY } from "@/pages/manufacturing/workOrdersPageModel";

interface IncomingSkuLine {
  sku: string;
  planned_units: number;
  remaining_to_schedule: number;
  remark: string | null;
  build_to_order: boolean;
}

interface IncomingSalesOrderRow {
  sales_order_id: number;
  sales_order_number: string | null;
  mv_id: number | null;
  location_id: number;
  location_label: string;
  ship_by_date: string | null;
  /** Header status from Megaventory ingest (`public.sales_order.status`). */
  status: string | null;
  ship_to_recipient: string | null;
  ship_to_address_line_1: string | null;
  ship_to_address_line_2: string | null;
  ship_to_address_line_3: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip: string | null;
  ship_to_country: string | null;
  sku_quantities: IncomingSkuLine[];
}

interface IncomingSalesOrdersApi {
  rows: IncomingSalesOrderRow[];
}

type IncomingOrderSortKey = "sales_order" | "site" | "status" | "ship_by" | "ship_to" | "lines";

interface MarkClosedResponse {
  ok: true;
  status: string;
}

function formatIncomingSalesOrderStatusLabel(raw: string): string {
  return salesOrderStatusLabel(raw);
}

function incomingOrderStatusBadgeTone(raw: string): BadgeTone {
  const norm = raw.trim().toLowerCase();
  if (norm.includes("cancel")) {
    return "danger";
  }
  if (norm.includes("invoice") || norm.includes("shipped") || norm.includes("fulfill")) {
    return "success";
  }
  if (norm === "approved" || norm === "verified" || norm.includes("complete")) {
    return "success";
  }
  if (norm.includes("pending") || norm.includes("draft") || norm.includes("partial")) {
    return "warning";
  }
  return "neutral";
}

/** Megaventory web UI — matches other hub links (`document=SalesOrder`; see Zap templates in repo). */
function megaventorySalesOrderUrl(mvId: number, salesOrderNumber: string | null): string {
  const name = encodeURIComponent(
    salesOrderNumber != null && salesOrderNumber.trim() !== ""
      ? salesOrderNumber.trim()
      : `Sales Order ${mvId}`,
  );
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=SalesOrder&id=${mvId}&name=${name}`;
}

/** Incoming Orders **Ship by MM-DD-YYYY** (Moment.js parity on Dashboard). */
function formatShipByDashboard(isoDay: string | null): string {
  if (isoDay == null || isoDay.trim() === "") {
    return "—";
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay.trim());
  if (!match) {
    return isoDay.trim();
  }
  const [, yyyy, mm, dd] = match;
  return `${mm}-${dd}-${yyyy}`;
}

function formatShipToOneLine(order: IncomingSalesOrderRow): string | null {
  const parts = [
    order.ship_to_recipient,
    order.ship_to_address_line_1,
    order.ship_to_address_line_2,
    order.ship_to_address_line_3,
    [
      order.ship_to_city,
      order.ship_to_state,
      order.ship_to_zip,
    ]
      .filter((piece) => piece != null && piece.trim() !== "")
      .join(" "),
    order.ship_to_country,
  ].filter((piece) => piece != null && String(piece).trim() !== "");
  return parts.length > 0 ? parts.map(String).join(", ") : null;
}

export function IncomingSalesOrdersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const [search, setSearch] = useState("");
  const [closeModalOrder, setCloseModalOrder] = useState<IncomingSalesOrderRow | null>(null);

  const query = useQuery<IncomingSalesOrdersApi, ApiError>({
    queryKey: ["manufacturing", "incoming-sales-orders"],
    queryFn: () => apiFetch<IncomingSalesOrdersApi>("/api/manufacturing/incoming-sales-orders"),
    refetchOnWindowFocus: false,
  });

  const markClosedMutation = useMutation<MarkClosedResponse, ApiError, number>({
    mutationFn: (salesOrderId) =>
      apiFetch<MarkClosedResponse>(`/api/manufacturing/incoming-sales-orders/${String(salesOrderId)}/mark-closed`, {
        method: "POST",
      }),
    onSuccess: async () => {
      setCloseModalOrder(null);
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "incoming-sales-orders"] });
    },
  });

  const normalizedSearch = search.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    const rows = query.data?.rows ?? [];
    if (normalizedSearch === "") return rows;

    const matchesSkuBlock = (order: IncomingSalesOrderRow): boolean =>
      order.sku_quantities.some((line) => {
        const blob = `${line.sku ?? ""} ${line.remark ?? ""}`.toLowerCase();
        return blob.includes(normalizedSearch);
      });

    return rows.filter((order) => {
      const hay = [
        order.sales_order_number,
        formatShipByDashboard(order.ship_by_date),
        formatShipToOneLine(order),
        order.location_label,
        order.status,
        order.status != null ? formatIncomingSalesOrderStatusLabel(order.status) : null,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (hay.includes(normalizedSearch)) return true;

      const idStr = String(order.sales_order_id);
      if (idStr.includes(normalizedSearch.replace(/\s+/g, ""))) return true;

      return matchesSkuBlock(order);
    });
  }, [query.data?.rows, normalizedSearch]);

  const incomingOrderColumns = useMemo((): HubDataTableColumn<IncomingSalesOrderRow, IncomingOrderSortKey>[] => {
    return [
      {
        id: "sales_order",
        header: "Sales order",
        sortKey: "sales_order",
        overflow: "nowrap",
        sortValue: (order) => order.sales_order_number ?? String(order.sales_order_id),
        cell: (order) => {
          const label =
            order.sales_order_number != null && order.sales_order_number.trim() !== ""
              ? order.sales_order_number.trim()
              : `id ${String(order.sales_order_id)}`;
          return (
            <div className="space-y-1">
              <span className="font-semibold tabular-nums text-slate-900">{label}</span>
              {order.mv_id != null ? (
                <a
                  href={megaventorySalesOrderUrl(order.mv_id, order.sales_order_number)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
                  onClick={(event) => event.stopPropagation()}
                >
                  Megaventory
                  <ExternalLink className="h-3 w-3" aria-hidden />
                </a>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "site",
        header: "Site",
        sortKey: "site",
        sortValue: (order) => order.location_label,
        cell: (order) => (
          <Badge tone="muted" className="shrink-0">
            {order.location_label.trim()}
          </Badge>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortKey: "status",
        sortValue: (order) => order.status ?? "",
        cell: (order) =>
          order.status != null && order.status.trim() !== "" ? (
            <Badge tone={incomingOrderStatusBadgeTone(order.status)}>
              {formatIncomingSalesOrderStatusLabel(order.status)}
            </Badge>
          ) : (
            "—"
          ),
      },
      {
        id: "ship_by",
        header: "Ship by",
        sortKey: "ship_by",
        overflow: "nowrap",
        sortValue: (order) => order.ship_by_date ?? "",
        cell: (order) => formatShipByDashboard(order.ship_by_date),
      },
      {
        id: "ship_to",
        header: "Ship to",
        sortKey: "ship_to",
        sortValue: (order) => formatShipToOneLine(order) ?? "",
        cell: (order) => (
          <HubDataTextCell overflow="truncate" value={formatShipToOneLine(order) ?? "—"} className="max-w-[16rem] text-xs" />
        ),
      },
      {
        id: "lines",
        header: "Lines",
        sortKey: "lines",
        sortValue: (order) => order.sku_quantities.map((line) => line.sku).join(" "),
        cell: (order) => (
          <table className="w-full min-w-[22rem] text-xs">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pr-3 font-medium">SKU</th>
                <th className="pr-3 font-medium">Supply</th>
                <th className="pr-3 text-right font-medium">Planned</th>
                <th className="pr-3 text-right font-medium">Remaining</th>
                <th className="font-medium">Remark</th>
              </tr>
            </thead>
            <tbody>
              {order.sku_quantities.map((line) => (
                <tr key={`${String(order.sales_order_id)}-${line.sku}`} className="align-top">
                  <td className="pr-3 font-mono">{line.sku}</td>
                  <td className="pr-3">
                    <Badge tone={line.build_to_order ? "info" : "neutral"}>
                      {line.build_to_order ? "Made to order" : "From stock"}
                    </Badge>
                  </td>
                  <td className="pr-3 text-right tabular-nums">{line.planned_units}</td>
                  <td className="pr-3 text-right tabular-nums">{line.remaining_to_schedule}</td>
                  <td>{line.remark?.trim() || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        sortValue: () => "",
        cell: (order) => (
          <div className="flex flex-wrap gap-2" onClick={(event) => event.stopPropagation()}>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
              <Button
                size="sm"
                variant="secondary"
                disabled={!hubWritesAllowed || markClosedMutation.isPending}
                onClick={() => setCloseModalOrder(order)}
              >
                Mark as closed
              </Button>
            </ReadOnlyHoverLayer>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
              <Button
                size="sm"
                variant="secondary"
                disabled={!hubWritesAllowed}
                onClick={() => navigate(buildManufacturingEditBatchPath({ salesOrderId: order.sales_order_id }))}
              >
                Add to batch
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        ),
      },
    ];
  }, [hubWritesAllowed, markClosedMutation.isPending, navigate]);

  const filtersActive = normalizedSearch !== "";

  return (
    <div className="space-y-4">
      {closeModalOrder != null ? (
        <DocumentModal
          title="Mark sales order closed"
          showCloseButton={false}
          description={
            <p className="text-sm text-slate-600">
              {closeModalOrder.mv_id == null
                ? "This closes the hub sales order. The order disappears from Incoming orders. Only use this when the order is truly finished and should no longer accept batch scheduling."
                : "This updates Megaventory to Closed, then updates the hub database. The order disappears from Incoming orders. Only use this when the order is truly finished and should no longer accept batch scheduling."}
            </p>
          }
          onClose={() => {
            if (!markClosedMutation.isPending) {
              setCloseModalOrder(null);
              markClosedMutation.reset();
            }
          }}
          shellClassName="max-w-lg"
        >
          <div className="space-y-4 px-6 py-4">
            <p className="text-sm text-slate-800">
              Confirm close for{" "}
              <span className="font-semibold">
                {closeModalOrder.sales_order_number != null && closeModalOrder.sales_order_number.trim() !== ""
                  ? closeModalOrder.sales_order_number.trim()
                  : `Sales order id ${String(closeModalOrder.sales_order_id)}`}
              </span>
              {closeModalOrder.mv_id != null ? (
                <span className="text-slate-600"> (Megaventory id {String(closeModalOrder.mv_id)})</span>
              ) : null}
              ?
            </p>
            {markClosedMutation.isError ? (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {markClosedMutation.error.message}
              </p>
            ) : null}
            <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  setCloseModalOrder(null);
                  markClosedMutation.reset();
                }}
                disabled={markClosedMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={markClosedMutation.isPending}
                onClick={() => {
                  markClosedMutation.mutate(closeModalOrder.sales_order_id);
                }}
              >
                {markClosedMutation.isPending ? "Closing…" : "Confirm close"}
              </Button>
            </div>
          </div>
        </DocumentModal>
      ) : null}

      <header className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div className="space-y-1">
          <HubPageTitle>Incoming orders</HubPageTitle>
          <p className="text-xs text-slate-500">
            Unscheduled sales orders with open line quantity (parity with Manufacturing Dashboard Incoming Orders).
          </p>
        </div>
        <p className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900">Orders:</span> {filteredRows.length}
          {normalizedSearch !== "" && query.data != null
            ? ` of ${String(query.data.rows.length)}`
            : null}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Filter by SO number, ship-to, SKU, remark…" />
        </CardBody>
      </Card>

      <HubDataTable
        title={
          query.isLoading
            ? "Loading incoming orders…"
            : filteredRows.length === 1
              ? "1 open order"
              : `${filteredRows.length} open orders`
        }
        columns={incomingOrderColumns}
        rows={filteredRows}
        rowKey={(order) => String(order.sales_order_id)}
        loading={query.isLoading}
        loadingMessage="Loading…"
        error={query.isError ? query.error.message : null}
        empty="No unfilled orders."
        isFiltered={filtersActive}
        filteredEmpty="No unfilled orders match the current filters."
      />
    </div>
  );
}
